import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Alert,
  Skeleton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Pagination,
  Checkbox,
  Tooltip,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { theme } from "../../../../Theme";
import { PROMO_COLORS } from "../../constants/colors";
import { CollapsibleSection } from "./CollapsibleSection";
import { EmptyState } from "./EmptyState";
import { fmtMoney, fmtDiscountDepth, dependencyPctColor } from "./helpers";

const PROMO_SPLIT_COLOR = theme.palette.primary.main;
const NON_PROMO_SPLIT_COLOR = "#94A3B8";

const SKU_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SKU_TABLE_DEFAULT_PAGE_SIZE = 10;

const PROMO_DEP_TABLE_COLUMNS = [
  { id: "select", label: "", numeric: false, noSort: true },
  { id: "sku", label: "SKU", numeric: false },
  { id: "dependencyPct", label: "Dependency %", numeric: true },
  { id: "valueSplit", label: "Value", numeric: true },
  { id: "volumeSplit", label: "Volume", numeric: true },
  { id: "avgDiscountDepth", label: "Avg discount depth", numeric: true },
];

function promoDepColumnAlign(colId) {
  if (colId === "dependencyPct" || colId === "avgDiscountDepth") return "center";
  return "left";
}

function promoDepDescendingComparator(a, b, orderBy) {
  let av;
  let bv;
  switch (orderBy) {
    case "sku":
      av = String(a.sku ?? "");
      bv = String(b.sku ?? "");
      return bv.localeCompare(av);
    case "dependencyPct":
      av = a.dependencyPct ?? -Infinity;
      bv = b.dependencyPct ?? -Infinity;
      break;
    case "valueSplit":
      av = (a.promoValueSales ?? 0) + (a.nonPromoValueSales ?? 0);
      bv = (b.promoValueSales ?? 0) + (b.nonPromoValueSales ?? 0);
      break;
    case "volumeSplit":
      av = (a.promoVolumeL ?? 0) + (a.nonPromoVolumeL ?? 0);
      bv = (b.promoVolumeL ?? 0) + (b.nonPromoVolumeL ?? 0);
      break;
    case "avgDiscountDepth":
      av = a.avgDiscountDepth ?? -Infinity;
      bv = b.avgDiscountDepth ?? -Infinity;
      break;
    default:
      av = a.dependencyPct ?? -Infinity;
      bv = b.dependencyPct ?? -Infinity;
  }
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function formatDiscountDepthCell(row) {
  if (row.avgDiscountDepth == null || Number.isNaN(Number(row.avgDiscountDepth))) {
    return "—";
  }
  return fmtDiscountDepth(row.avgDiscountDepth);
}

function discountDepthTooltip(row) {
  if (row.avgDiscountDepth == null || Number.isNaN(Number(row.avgDiscountDepth))) {
    return "Discount depth unavailable (insufficient non-promo baseline data)";
  }
  return `Average discount depth: ${formatDiscountDepthCell(row)}`;
}

function SplitValueRow({ label, value, color, pct }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
      <Box
        sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, fontWeight: 600, flexShrink: 0, minWidth: 52 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={700}
        noWrap
        title={value}
        sx={{ fontSize: 11, flex: 1, minWidth: 0, textAlign: "right" }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontSize: 10, flexShrink: 0, minWidth: 32, textAlign: "right" }}
      >
        {pct != null ? `${Math.round(pct)}%` : "—"}
      </Typography>
    </Stack>
  );
}

function PromoNonPromoSplitBar({
  promoAmount,
  nonPromoAmount,
  formatAmount = (v) =>
    Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }),
  unitSuffix = "",
}) {
  const total = Number(promoAmount) + Number(nonPromoAmount);
  const promoPct = total > 0 ? (Number(promoAmount) / total) * 100 : 0;
  const nonPromoPct = total > 0 ? 100 - promoPct : 0;
  const fmt = (v) => `${formatAmount(v)}${unitSuffix}`;

  const segmentSx = (pct, bgcolor) => ({
    width: `${pct}%`,
    minWidth: pct > 0 ? 3 : 0,
    bgcolor,
  });

  return (
    <Box sx={{ minWidth: 210, maxWidth: 320 }}>
      <Stack
        direction="row"
        height={8}
        borderRadius={1}
        overflow="hidden"
        sx={{ bgcolor: "#EEF1F4", mb: 0.75 }}
      >
        <Tooltip title={`Promo: ${fmt(promoAmount)} (${Math.round(promoPct)}%)`} arrow>
          <Box sx={segmentSx(promoPct, PROMO_SPLIT_COLOR)} />
        </Tooltip>
        <Tooltip
          title={`Non-promo: ${fmt(nonPromoAmount)} (${Math.round(nonPromoPct)}%)`}
          arrow
        >
          <Box sx={segmentSx(nonPromoPct, NON_PROMO_SPLIT_COLOR)} />
        </Tooltip>
      </Stack>
      <Stack spacing={0.35}>
        <SplitValueRow
          label="Promo"
          value={fmt(promoAmount)}
          color={PROMO_SPLIT_COLOR}
          pct={promoPct}
        />
        <SplitValueRow
          label="Non-promo"
          value={fmt(nonPromoAmount)}
          color={NON_PROMO_SPLIT_COLOR}
          pct={nonPromoPct}
        />
      </Stack>
    </Box>
  );
}

export function PromoDependencyAnalysisPanel({
  skuPromoDependency,
  loading,
  error,
  selectedSkus,
  onToggleSku,
  filterBar,
}) {
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("dependencyPct");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(SKU_TABLE_DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [search, skuPromoDependency]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return skuPromoDependency;
    return skuPromoDependency.filter((r) =>
      String(r.sku).toLowerCase().includes(q)
    );
  }, [skuPromoDependency, search]);

  const sortedRows = useMemo(() => {
    const sorted = [...filtered].sort((a, b) =>
      promoDepDescendingComparator(a, b, orderBy)
    );
    return order === "asc" ? sorted.reverse() : sorted;
  }, [filtered, order, orderBy]);

  const totalFiltered = sortedRows.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / rowsPerPage));

  useEffect(() => {
    if (page > 0 && page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const paginationCount = Math.ceil(totalFiltered / rowsPerPage);
  const hasData = skuPromoDependency.length > 0;

  const allVisible = sortedRows.map((r) => r.sku);
  const allVisibleSelected =
    allVisible.length > 0 && allVisible.every((s) => selectedSkus.has(s));
  const someVisibleSelected = allVisible.some((s) => selectedSkus.has(s));

  const handleSelectAllVisible = () => {
    if (allVisibleSelected) {
      allVisible.forEach((s) => onToggleSku(s, false));
    } else {
      allVisible.forEach((s) => onToggleSku(s, true));
    }
  };

  return (
    <CollapsibleSection
      icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
      title="Promo Dependency Analysis"
      subtitle="Per-SKU promo vs non-promo value and volume from historical performance — select SKUs to send to Simulator or Optimizer"
      filterBar={filterBar}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && hasData && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={2.5}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap alignItems="center">
              {[
                { label: "Low dependency (≤ 40%)", color: PROMO_COLORS.safe },
                { label: "Medium (41–60%)", color: PROMO_COLORS.warning },
                { label: "High (> 60%)", color: PROMO_COLORS.threat },
              ].map(({ label, color }) => (
                <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
          <TextField
            size="small"
            placeholder="Search SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 360 }}
          />
        </Stack>
      )}

      {loading ? (
        <Stack spacing={1.25}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} />
          ))}
        </Stack>
      ) : !hasData ? (
        <EmptyState message="No promo dependency data for the selected filters." />
      ) : sortedRows.length === 0 ? (
        <EmptyState message="No SKUs match your search." />
      ) : (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="ph-promo-dep-rows-per-page">Rows per page</InputLabel>
              <Select
                labelId="ph-promo-dep-rows-per-page"
                label="Rows per page"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                {SKU_TABLE_PAGE_SIZE_OPTIONS.map((n) => (
                  <MenuItem key={`promo-dep-rpp-${n}`} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TableContainer
            sx={{ borderRadius: 1.5, border: "1px solid #EEF1F4", overflow: "auto" }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{ "& .MuiTableCell-root": { borderColor: "#EEF1F4" } }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#F4F6F9" }}>
                  {PROMO_DEP_TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={promoDepColumnAlign(col.id)}
                      sortDirection={!col.noSort && orderBy === col.id ? order : false}
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#475569",
                        whiteSpace: "nowrap",
                        py: 1.25,
                        px: col.id === "select" ? 1 : undefined,
                        width: col.id === "select" ? 44 : undefined,
                        minWidth:
                          col.id === "select"
                            ? 44
                            : col.id === "sku"
                            ? 180
                            : col.id === "valueSplit" || col.id === "volumeSplit"
                            ? 250
                            : col.id === "avgDiscountDepth"
                            ? 130
                            : 120,
                      }}
                    >
                      {col.id === "select" ? (
                        <Tooltip
                          title={allVisibleSelected ? "Deselect all" : "Select all"}
                          arrow
                        >
                          <Checkbox
                            size="small"
                            checked={allVisibleSelected}
                            indeterminate={someVisibleSelected && !allVisibleSelected}
                            onChange={handleSelectAllVisible}
                            sx={{
                              p: 0.25,
                              color: "#CBD5E1",
                              "&.Mui-checked": { color: theme.palette.primary.main },
                              "&.MuiCheckbox-indeterminate": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        </Tooltip>
                      ) : col.noSort ? (
                        col.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : "asc"}
                          onClick={() => handleRequestSort(col.id)}
                          sx={
                            col.id === "dependencyPct" ||
                            col.id === "avgDiscountDepth"
                              ? { justifyContent: "center", width: "100%" }
                              : undefined
                          }
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const depColor = dependencyPctColor(row.dependencyPct);
                  const isChecked = selectedSkus.has(row.sku);
                  return (
                    <TableRow
                      key={row.sku}
                      hover
                      onClick={() => onToggleSku(row.sku, !isChecked)}
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        cursor: "pointer",
                        bgcolor: isChecked
                          ? `${theme.palette.primary.main}08`
                          : undefined,
                        "&:hover": {
                          bgcolor: isChecked
                            ? `${theme.palette.primary.main}12 !important`
                            : undefined,
                        },
                      }}
                    >
                      <TableCell
                        sx={{ py: 1.25, px: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={(e) => onToggleSku(row.sku, e.target.checked)}
                          sx={{
                            p: 0.25,
                            color: "#CBD5E1",
                            "&.Mui-checked": { color: theme.palette.primary.main },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          title={row.sku}
                          sx={{ lineHeight: 1.35 }}
                        >
                          {row.sku}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ textAlign: "center", color: depColor }}
                        >
                          {row.dependencyPct != null ? `${row.dependencyPct}%` : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <PromoNonPromoSplitBar
                          promoAmount={row.promoValueSales}
                          nonPromoAmount={row.nonPromoValueSales}
                          formatAmount={fmtMoney}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <PromoNonPromoSplitBar
                          promoAmount={row.promoVolumeL}
                          nonPromoAmount={row.nonPromoVolumeL}
                          formatAmount={(v) =>
                            Number(v).toLocaleString("en-US", {
                              maximumFractionDigits: 0,
                            })
                          }
                          unitSuffix=" L"
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Tooltip title={discountDepthTooltip(row)} arrow>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={
                              row.avgDiscountDepth != null
                                ? "text.primary"
                                : "text.disabled"
                            }
                            sx={{ textAlign: "center" }}
                          >
                            {formatDiscountDepthCell(row)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {paginationCount >= 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
                borderTop: "1px solid #EEF1F4",
              }}
            >
              <Pagination
                count={paginationCount}
                page={page + 1}
                onChange={(_e, value) => setPage(value - 1)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root": { fontWeight: 500, fontSize: 13 },
                  "& .MuiPaginationItem-page.Mui-selected": { fontWeight: 700 },
                }}
              />
            </Box>
          )}
        </>
      )}
    </CollapsibleSection>
  );
}
